import { SITE_DOMAIN } from '../config';

/**
 * Add-to-calendar helpers for Revive Nights.
 *
 * Events with a confirmed date but an unconfirmed time ("Time TBD") become
 * all-day entries; confirmed times become 2-hour timed entries pinned to
 * America/New_York so the invite is correct no matter where it's opened.
 */

const TZID = 'America/New_York';
const DURATION_HOURS = 2;

function parseDateParts(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
}

/** '6:00 PM' -> { h: 18, min: 0 }; null when the label isn't a real time. */
function parseTimeLabel(label) {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((label || '').trim());
  if (!match) return null;
  let h = Number(match[1]) % 12;
  if (match[3].toUpperCase() === 'PM') h += 12;
  return { h, min: Number(match[2]) };
}

const pad = (n) => String(n).padStart(2, '0');

function icsLocalStamp({ y, m, d }, { h, min }) {
  return `${y}${pad(m)}${pad(d)}T${pad(h)}${pad(min)}00`;
}

function icsDateStamp({ y, m, d }) {
  return `${y}${pad(m)}${pad(d)}`;
}

function nextDay(parts) {
  const dt = new Date(parts.y, parts.m - 1, parts.d + 1);
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

function eventTitle(event) {
  return event.isSabbath ? event.edition : `Revive Night: ${event.edition}`;
}

function eventDescription(event) {
  const lines = [];
  if (event.theme && event.theme !== 'TBD') lines.push(`Theme: ${event.theme}`);
  if (event.verseRef && event.verseText) {
    lines.push(`${event.verseRef} - "${event.verseText}"`);
  }
  lines.push(`Details: https://${SITE_DOMAIN}/nights`);
  return lines.join('\\n');
}

/** Escape commas/semicolons per RFC 5545 text rules. */
function icsText(value) {
  // RFC 5545: newlines in TEXT must be the literal \n escape — raw CR/LF
  // would start new ICS content lines (property injection). The server
  // strips control chars from stored events too; this is defense in depth.
  return String(value)
    .replace(/\\(?!n)/g, '\\\\')
    .replace(/\r\n|[\r\n]/g, '\\n')
    .replace(/[,;]/g, (c) => `\\${c}`);
}

export function buildIcs(event) {
  const start = parseDateParts(event.date);
  const time = parseTimeLabel(event.timeLabel);

  const dtLines = time
    ? [
        `DTSTART;TZID=${TZID}:${icsLocalStamp(start, time)}`,
        `DTEND;TZID=${TZID}:${icsLocalStamp(start, {
          h: time.h + DURATION_HOURS,
          min: time.min,
        })}`,
      ]
    : [
        `DTSTART;VALUE=DATE:${icsDateStamp(start)}`,
        `DTEND;VALUE=DATE:${icsDateStamp(nextDay(start))}`,
      ];

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Revive Worship//Revive Nights//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@${SITE_DOMAIN}`,
    `DTSTAMP:${icsDateStamp(start)}T000000Z`,
    ...dtLines,
    `SUMMARY:${icsText(eventTitle(event))}`,
    `DESCRIPTION:${icsText(eventDescription(event))}`,
    `LOCATION:${icsText(event.locationName || 'Pittsburgh, PA')}`,
    `URL:https://${SITE_DOMAIN}/nights`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

export function downloadIcs(event) {
  const blob = new Blob([buildIcs(event)], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `revive-night-${event.id}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function googleCalendarUrl(event) {
  const start = parseDateParts(event.date);
  const time = parseTimeLabel(event.timeLabel);

  const dates = time
    ? `${icsLocalStamp(start, time)}/${icsLocalStamp(start, {
        h: time.h + DURATION_HOURS,
        min: time.min,
      })}`
    : `${icsDateStamp(start)}/${icsDateStamp(nextDay(start))}`;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventTitle(event),
    dates,
    details: eventDescription(event).replace(/\\n/g, '\n'),
    location: event.locationName || 'Pittsburgh, PA',
    ctz: TZID,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
